// Client/src/store/openingHoursStore.ts

import { create } from 'zustand';
import { sessionManager } from '../core/SessionManager';
import { fetchWithAuth } from './fetchClient';

// Zeitspanne für einen einzelnen Öffnungszeitraum (Öffnen bis Schließen)
export interface TimeRange {
  open: string;
  close: string;
}

// Tagestypen (Englische Bezeichnungen wie im Server)
export type DayOfWeek = 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';

// Struktur für reguläre wöchentliche Öffnungszeiten
export type WeeklyOpeningHours = Record<DayOfWeek, TimeRange[]>;

// Ein spezieller Öffnungstag (z.B. Feiertag mit abweichenden Öffnungszeiten)
export interface ExtraOpeningDay {
  date: string; // ISO Format "YYYY-MM-DD"
  timeRanges: TimeRange[];
}

// Antwort der API für Öffnungszeiten
export interface OpeningHoursResponse {
  storeId: string;
  storeName: string;
  date: string; // ISO Format "YYYY-MM-DD" 
  dayOfWeek: string; // Deutscher Name des Wochentags
  isOpen: boolean;
  openingHours: TimeRange[];
  isSpecialOpeningDay: boolean;
  isClosingDay: boolean;
}

// Zustand für die Öffnungszeiten
interface OpeningHoursState {
  // Aktuelle Öffnungszeiten
  currentOpeningHours: OpeningHoursResponse | null;
  
  // Wird geladen?
  isLoading: boolean;
  error: string | null;
  
  // Cache für jeden Store (storeId => Öffnungszeiten)
  cachedOpeningHours: Record<string, {
    data: OpeningHoursResponse;
    timestamp: number;
  }>;
  
  // Cache-Zeit in Millisekunden (24 Stunden)
  readonly cacheDuration: number;
  
  // Öffnungszeiten für den ausgewählten Store laden
  fetchOpeningHours: (storeId: string) => Promise<OpeningHoursResponse | null>;
  
  // Aktuellen Status für einen Store abrufen (aus Cache oder laden)
  getOpeningHours: (storeId?: string | null) => Promise<OpeningHoursResponse | null>;
  
  // Cache löschen für einen bestimmten Store oder komplett
  clearCache: (storeId?: string) => void;
}

/**
 * Store für die Öffnungszeiten der Filialen.
 * Ermöglicht das Abfragen der aktuellen Öffnungszeiten und speichert diese im Cache.
 */
export const useOpeningHoursStore = create<OpeningHoursState>((set, get) => ({
  // Initialer Zustand
  currentOpeningHours: null,
  isLoading: false,
  error: null,
  cachedOpeningHours: {},
  
  // Standardmäßig 24 Stunden Cache
  cacheDuration: 24 * 60 * 60 * 1000,
  
  /**
   * Öffnungszeiten für eine bestimmte Filiale laden
   * @param storeId ID der Filiale
   * @returns Die Öffnungszeiten oder null bei Fehler
   */
  fetchOpeningHours: async (storeId: string): Promise<OpeningHoursResponse | null> => {
    try {
      set({ isLoading: true, error: null });
      
      // API-Call zur Abfrage der aktuellen Öffnungszeiten
      const response = await fetchWithAuth(`/api/stores/${storeId}/opening-hours/today`);
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `Fehler beim Laden der Öffnungszeiten für Markt ${storeId}`);
      }
      
      const data: OpeningHoursResponse = await response.json();
      
      // Im Cache speichern
      const cachedData = {
        data,
        timestamp: Date.now()
      };
      
      set((state) => ({
        isLoading: false,
        currentOpeningHours: data,
        cachedOpeningHours: {
          ...state.cachedOpeningHours,
          [storeId]: cachedData
        }
      }));
      
      return data;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unbekannter Fehler';
      set({ isLoading: false, error: errorMessage });
      return null;
    }
  },
  
  /**
   * Öffnungszeiten für einen Store abrufen, entweder aus dem Cache oder neu laden.
   * Wenn kein storeId angegeben ist, wird versucht, die ID aus dem authStore zu holen.
   * @param storeId Optional: ID der Filiale, sonst wird authStore.selectedStoreId verwendet
   * @returns Die Öffnungszeiten oder null bei Fehler
   */
  getOpeningHours: async (storeId?: string | null): Promise<OpeningHoursResponse | null> => {
    // Filial-ID ermitteln (übergebene ID oder aus authStore)
    let finalStoreId = storeId;
    
    if (!finalStoreId) {
      finalStoreId = sessionManager.getSnapshot().selectedStoreId;
    }
    
    // Abbrechen, wenn keine Filial-ID verfügbar ist
    if (!finalStoreId) {
      set({ error: 'Keine Filial-ID verfügbar.' });
      return null;
    }
    
    // Prüfen, ob wir einen gültigen Cache-Eintrag haben
    const cachedEntry = get().cachedOpeningHours[finalStoreId];
    const now = Date.now();
    
    if (cachedEntry && (now - cachedEntry.timestamp) < get().cacheDuration) {
      // Cache ist noch gültig, direkt verwenden
      set({ currentOpeningHours: cachedEntry.data, error: null });
      return cachedEntry.data;
    }
    
    // Cache ist nicht verfügbar oder abgelaufen => neu laden
    return await get().fetchOpeningHours(finalStoreId);
  },
  
  /**
   * Cache für einen bestimmten Store oder komplett löschen
   * @param storeId Optional: ID der Filiale, sonst wird der gesamte Cache gelöscht
   */
  clearCache: (storeId?: string): void => {
    if (storeId) {
      // Nur den Cache für die angegebene Filiale löschen
      set((state) => {
        const newCache = { ...state.cachedOpeningHours };
        delete newCache[storeId];
        return { cachedOpeningHours: newCache };
      });
    } else {
      // Gesamten Cache löschen
      set({ cachedOpeningHours: {} });
    }
  }
}));

/**
 * Hook, der true zurückgibt, wenn die Filiale aktuell geöffnet ist
 * @returns Boolean, ob die ausgewählte Filiale aktuell geöffnet ist
 */
export const useIsStoreOpen = (): boolean => {
  const currentOpeningHours = useOpeningHoursStore((state) => state.currentOpeningHours);
  return currentOpeningHours?.isOpen ?? false;
};

/**
 * Überprüft, ob die aktuelle Uhrzeit innerhalb der Öffnungszeiten liegt
 * @param timeRanges Array von Öffnungszeitenspannen
 * @returns true, wenn die aktuelle Zeit in einer der Zeitspannen liegt
 */
export const isTimeInOpeningHours = (timeRanges: TimeRange[]): boolean => {
  if (!timeRanges || timeRanges.length === 0) return false;
  
  const now = new Date();
  const currentHours = now.getHours().toString().padStart(2, '0');
  const currentMinutes = now.getMinutes().toString().padStart(2, '0');
  const currentTimeStr = `${currentHours}:${currentMinutes}`;
  
  // Prüfen, ob die aktuelle Zeit in einer der Zeitspannen liegt
  return timeRanges.some((range) => {
    return currentTimeStr >= range.open && currentTimeStr <= range.close;
  });
};
